/**
 * THE COLUMN BESIDE A CTS TRIANGLE: what the next few bars hold.
 *
 * <h2>What it is</h2>
 *
 * A mark that survived the cooldown stands for itself AND for whatever the fold removed behind
 * it. That was invisible, so a reader deciding whether to act on a signal could not tell a lone
 * event from the first of four. This draws the window as a small vertical column of glyphs next
 * to the triangle:
 *
 * <pre>
 *   a DASH  for a following bar that carries a signal
 *   a DOT   for a following bar that does not
 * </pre>
 *
 * So with signals on the 2nd and 3rd bars after the mark, the column reads
 * {@code . - - .} — top to bottom, nearest bar first.
 *
 * <h2>⚠ NOTHING IS DRAWN WHEN NOTHING WAS SUPPRESSED</h2>
 *
 * A column of four dots would be noise on every isolated mark, which is most of them. The glyph
 * exists to answer "is there another one coming", so the absence of the column IS the answer
 * "no" — and a chart where most marks carry no column makes the ones that do stand out, which is
 * the whole point.
 *
 * <h2>⚠ A COLUMN RATHER THAN A ROW, and that is not a taste</h2>
 *
 * A horizontal row of four glyphs would span four bars' width and collide with the next mark's
 * triangle — the denser the data, the worse it reads, which is exactly backwards. A column stays
 * inside one bar's width at any zoom.
 *
 * <h2>⚠ IT IS NOT LOOK-AHEAD, and that is worth stating because it looks like it</h2>
 *
 * CTS is pure ephemeris and the datasets run to 2100, so "a signal lands two bars from here" is
 * knowable today and was knowable a century ago. It is the same category as the forward CTS marks
 * the chart already draws past the last traded bar: calendar structure, not an answer. The
 * repository states the distinction — calendar structure may stand ahead of the playhead because
 * it was known at bar one; a trade marker may not, because it IS the answer.
 *
 * <p>What the column must therefore never carry is anything derived from PRICE. It reports which
 * bars the sky marks, and nothing about what those bars did.
 */

/** A following bar that carries a signal. */
export const DASH = 'dash';

/** A following bar that does not. */
export const DOT = 'dot';

/**
 * The pattern for the {@code window} bars after {@code barIndex}.
 *
 * @param barIndex the bar the surviving mark sits on
 * @param flaggedBars a Set of every bar index carrying a signal, BEFORE folding
 * @param window how many bars ahead to report — the cooldown, normally
 * @returns an array of DASH/DOT, nearest bar first, or EMPTY when no following bar is flagged
 */
export function clusterPattern(barIndex, flaggedBars, window) {
  if (!Number.isFinite(barIndex) || !(flaggedBars instanceof Set) || !(window > 0)) return [];
  const out = [];
  let anySignal = false;
  for (let k = 1; k <= window; k++) {
    const hit = flaggedBars.has(barIndex + k);
    if (hit) anySignal = true;
    out.push(hit ? DASH : DOT);
  }
  // ⚠ ALL-OR-NOTHING, deliberately. A trailing run of dots is KEPT -- `. - - .` says the fourth
  // bar is clear, which is information -- but a pattern of dots ALONE is dropped entirely rather
  // than trimmed to nothing, because trimming would leave a one-dot column that reads as a signal.
  return anySignal ? out : [];
}

/**
 * The glyph points to draw beside one mark.
 *
 * <h2>Geometry</h2>
 *
 * The column sits DIRECTLY OVER the triangle — same x, same axis, stepping away from it.
 *
 * <p>⚠ IT USED TO BE PINNED TO THE RAIL WHILE THE TRIANGLE FOLLOWED ITS PRICE, which is why it
 * "disappeared on zooming". A CTS mark on a revealed bar is drawn AT the bar's own high or low on
 * the price axis; the column carried no price at all, so the renderer routed it to the 0..1 rail
 * by rule. Measured on 1993-02-02 at a 21-bar zoom: the triangle at pixel y <b>1830.6</b>, its own
 * column at <b>851.5</b> — <b>979 pixels apart on the same bar</b>.
 *
 * <p>It looked right zoomed out for an arithmetic reason rather than a lucky one: across 8,745
 * bars the price axis spans 45 to 780 in about 360px, so an early-1990s price of 44 lands within a
 * few pixels of the rail and the column appears to be attached. Zoom in and the axis rescales to
 * the visible range, the triangle moves to wherever its price now falls, and the column stays on
 * the rail. **The error breathes with the zoom**, which is the same failure mode as the sideways
 * pixel offset this file already records — and it is why a reader sees it come and go rather than
 * simply being wrong.
 *
 * <p>So a glyph now inherits the mark's own PLACEMENT: its price when the mark has one, and its
 * `position` so the column steps to the SAME SIDE the triangle is on — further below the low for
 * an up signal, further above the high for a down one. Never across the candle. Where the mark
 * itself is on the rail (a bar the replay has not revealed, or a forward CTS date past the last
 * traded bar) the glyph has no price either, so both stay on the rail together.
 *
 * <h2>⚠ A PRICED GLYPH MUST USE `offsetPx`, NOT `offsetYPx`</h2>
 *
 * The renderer's two placements read different fields. A rail-anchored point gets
 * `[offsetXPx, offsetYPx]` verbatim; a priced one gets `[0, sign * offsetPx]` with the sign taken
 * from `position`, and `offsetXPx`/`offsetYPx` are not consulted at all. A priced glyph carrying
 * only `offsetYPx` would therefore stack at the overlay's default 12px — four glyphs on one pixel,
 * on top of the triangle.
 *
 * <h2>Reading order is TOP TO BOTTOM, nearest bar first, both ways up</h2>
 *
 * The column reads like a list whichever side of the bar it is on, which is what makes the two
 * cases one convention rather than two. For an UPWARD column the top is the largest offset, so the
 * nearest bar takes it; for a DOWNWARD column the top is the SMALLEST, so the nearest bar takes
 * that instead. The index arithmetic differs precisely so the reading does not.
 *
 * <p>⚠ AND THE COLUMN IS NEVER OFFSET SIDEWAYS. Nine pixels looks like a hair's breadth and on a
 * zoomed-out chart a bar is about three pixels wide, so the column once landed roughly THREE BARS
 * to the right of the mark it describes — reported as "the dash-dots are not aligned". A column is
 * only unambiguously attached to its own bar at an offset of zero.
 *
 * <p>A dash is a flat rectangle rather than a character: {@code symbolSize} takes `[w, h]`, so a
 * 6x1.5 rect is a dash at any zoom and needs no font. A dot is a 2px circle.
 *
 * @param opts.date the mark's own date — REQUIRED, see the note below
 * @param opts.price the mark's price, when it has one. Absent, the column stays on the rail
 * @param opts.position the mark's own `'below'` / `'above'`, so the column steps the same way
 */
export function clusterGlyphPoints(hit, flaggedBars, window, opts = {}) {
  const pattern = clusterPattern(hit && hit.barIndex, flaggedBars, window);
  if (pattern.length === 0) return [];
  /*
   * ⚠ THE DATE COMES FROM THE CALLER, NOT FROM THE HIT, and the first version got this wrong.
   *
   * A `reversalBars` hit is `{ barIndex, dates, exact }` -- `dates` is the LIST of CTS days that
   * landed on the bar, and there is no `date` field at all. Reading `hit.date` gave `undefined`
   * on every point, and the renderer's own filter (`p.date !== undefined`) then dropped all of
   * them. The glyphs were built correctly, passed their unit tests, and never reached the chart.
   *
   * The triangles beside them resolve it the same way: `source.dates[hit.barIndex]`. The caller
   * owns the bar-index-to-date mapping, so the caller passes it.
   */
  const date = opts.date;
  if (!date) return [];

  // ZERO: the column stands on the mark's own bar. See the note above -- a sideways pixel offset
  // is a different number of bars at every zoom, so there is no non-zero value that is "aligned".
  const x = Number.isFinite(opts.offsetXPx) ? opts.offsetXPx : 0;
  const step = Number.isFinite(opts.stepPx) ? opts.stepPx : 5;
  // Clear of the mark AND of the badge chip above it; the caller supplies the real clearance,
  // because only the caller knows the symbol size and the badge offset it has to clear.
  const base = Number.isFinite(opts.basePx) ? opts.basePx : 3;
  const colour = opts.color || 'rgba(120, 144, 156, 0.95)';

  /*
   * THE MARK'S OWN PLACEMENT, inherited rather than chosen.
   *
   * A finite price puts the column on the price axis beside the triangle; its absence leaves both
   * on the rail. `position` then decides which way the column steps, so it never crosses the
   * candle the triangle is pointing at.
   */
  const price = Number.isFinite(opts.price) ? opts.price : null;
  const down = price !== null && opts.position === 'below';

  return pattern.map((kind, i) => ({
    date,
    ...(price !== null ? { price, position: opts.position } : {}),
    // The two placements read DIFFERENT FIELDS -- see the note above. A rail point is positioned
    // by the pair verbatim; a priced one by a magnitude whose sign comes from `position`.
    ...(price !== null
      // Top of the column is the SMALLEST offset going down and the LARGEST going up, so the
      // nearest bar leads the reading either way.
      ? { offsetPx: base + (down ? i : pattern.length - 1 - i) * step }
      : {
        // Negative is UP in the renderer's symbolOffset. The LAST element sits nearest the rail,
        // so index 0 -- the next bar -- ends up at the top of the column.
        offsetXPx: x,
        offsetYPx: -(base + (pattern.length - 1 - i) * step),
      }),
    symbol: kind === DASH ? 'rect' : 'circle',
    /*
     * ⚠ THE SIZE IS A PER-POINT FIELD THE pivotMarkers BRANCH DOES NOT READ. It resolves
     * `p.symbol` but takes the SIZE from the series, so these would render at the triangles' own
     * 18px -- four huge blocks beside each mark. `symbolSizePx` is read by the renderer change
     * that accompanies this file; the plain `symbolSize` name is deliberately NOT used, so that a
     * reader who greps for it does not conclude the standard field works here.
     */
    symbolSizePx: kind === DASH ? [6, 1.5] : 2,
    color: colour,
    // ⚠ NO `simSignal`, and no tooltip of its own. These are annotation, not marks: a click on one
    // must not open the Trade Simulator for a bar the reader did not choose, and a hover must not
    // compete with the triangle's own tooltip a few pixels away.
    cluster: true,
  }));
}

/** A human sentence for the triangle's tooltip — how many are coming, and how soon. */
export function clusterTooltipLine(barIndex, flaggedBars, window) {
  const pattern = clusterPattern(barIndex, flaggedBars, window);
  if (pattern.length === 0) return null;
  const offsets = pattern
    .map((kind, i) => (kind === DASH ? i + 1 : null))
    .filter((n) => n !== null);
  return `${offsets.length} more within ${window} bars: `
    + offsets.map((n) => `+${n}`).join(', ');
}
